import { Container, BlurFilter } from '../../../vendor/pixi.min.mjs';

/**
 * Drifting sky clouds over the top portion of the Snake playfield.
 *
 * Four sprites (two large 200×100, two small 120×60) are spread across
 * the top ~⅓ of the play arena and drift slowly rightward. When a cloud
 * scrolls fully past the right edge it teleports back to just outside
 * the left edge so the loop is seamless without ever leaving an empty
 * sky.
 *
 * The whole thing is purely cosmetic — the layer sits behind every
 * gameplay element and never participates in collisions, occupies()
 * checks or excluded-set logic.
 */

/** Cloud definitions — slot, render size (px), drift speed (px / frame at 60 fps).
 *
 * Speeds are deliberately VERY close (0.34..0.42, ~24 % spread) so faster
 * clouds can't lap slower ones and pile up at the right edge over the
 * course of a long run. Earlier tuning used 0.22..0.55 (2.5× ratio) and
 * the entire flock collapsed into the right corner within a couple of
 * minutes — see the wrap helper below for the second half of the fix.
 *
 * Y bands are also spread across the full sky strip (10 %, 35 %, 65 %,
 * 90 %) so even if two clouds momentarily share an X they don't sit on
 * top of each other vertically.
 */
const CLOUD_DEFS = [
    { slot: 'snake.sky.cloud.large.1', w: 200, h: 100, speed: 0.38, yFrac: 0.10, alpha: 0.92 },
    { slot: 'snake.sky.cloud.small.1', w: 120, h:  60, speed: 0.42, yFrac: 0.35, alpha: 0.85 },
    { slot: 'snake.sky.cloud.large.2', w: 200, h: 100, speed: 0.34, yFrac: 0.65, alpha: 0.88 },
    { slot: 'snake.sky.cloud.small.2', w: 120, h:  60, speed: 0.40, yFrac: 0.90, alpha: 0.85 },
];

/** Fraction of the playfield height the clouds may inhabit (top band). */
const SKY_BAND_FRAC = 0.32;

/**
 * Default Gaussian blur strength when {@link SnakeClouds} is built with
 * `blurred: true`. Tuned to feel like a shallow depth-of-field "out of
 * focus" sky — strong enough to read as soft (cloud edges turn into a
 * gentle haze) without smearing them into unrecognisable smudges.
 */
const CLOUD_BLUR_STRENGTH = 8;
/**
 * Quality vs. cost trade-off — `quality = 2` gives two blur passes per
 * axis which is plenty for soft, animated clouds and stays cheap on
 * mid-range mobile GPUs. Bumping to 4 looks nearly identical and
 * doubles the fill cost, so 2 is the sweet spot.
 */
const CLOUD_BLUR_QUALITY = 2;

/** Minimum clearance (× cloud width) between a wrapping cloud and the
 *  leftmost existing cloud. Keeps the flock evenly spaced over time —
 *  without this guard, tiny drift in delta-times causes the staggered
 *  start positions to collapse into clumps after a few minutes. */
const WRAP_MIN_GAP_FACTOR = 1.3;

export class SnakeClouds {
    /** @type {Container} */
    container = new Container();

    /** @type {{ sprite: any, speed: number, w: number }[]} */
    _clouds = [];

    /** Drift box in world coords: { x, y, w, h }. */
    _bounds = { x: 0, y: 0, w: 0, h: 0 };

    /**
     * @param {*} themeManager
     * @param {{ x:number, y:number, w:number, h:number }} bounds
     * @param {{ blurred?: boolean }} [options]
     *        `blurred: true` applies a Gaussian {@link BlurFilter} to the
     *        whole cloud layer so the sky reads as out-of-focus while
     *        gameplay (which lives outside this container) stays sharp.
     */
    constructor(themeManager, bounds, options = {}) {
        this.container.eventMode = 'none'; // never intercept input
        this._setBounds(bounds);
        this._build(themeManager);
        if (options.blurred) this._applyBlur();
    }

    /**
     * Apply a soft Gaussian blur to the entire cloud container. Done at
     * the container level (not per-sprite) so the GPU only has to run
     * the filter pass once per frame instead of four times.
     */
    _applyBlur() {
        try {
            const filter = new BlurFilter({
                strength: CLOUD_BLUR_STRENGTH,
                quality:  CLOUD_BLUR_QUALITY,
            });
            this.container.filters = [filter];
        } catch {
            // BlurFilter unavailable in this Pixi build / older renderer
            // — silently fall back to crisp clouds rather than crashing.
        }
    }

    /**
     * Move each cloud rightward; wrap to just outside the left edge once
     * a cloud has fully exited the right edge. Wrap target X is clamped
     * so the cloud always lands BEHIND the current leftmost cloud with
     * at least `WRAP_MIN_GAP_FACTOR × cloud.w` of clearance — this is
     * the safeguard that stops the flock from clumping over long runs.
     *
     * @param {number} delta — Pixi ticker delta (1.0 == 60 fps frame).
     */
    update(delta) {
        if (!this._clouds.length) return;
        const leftEdge  = this._bounds.x;
        const rightEdge = this._bounds.x + this._bounds.w;
        for (const c of this._clouds) {
            const s = c.sprite;
            if (!s || s.destroyed) continue;
            s.x += c.speed * delta;
            // Sprites are anchor-centred so `x - w/2` is the LEFT edge.
            if (s.x - c.w / 2 > rightEdge) {
                s.x = this._pickWrapX(c);
            }
        }
    }

    /**
     * Choose a left-edge wrap position that's at least one cloud-width
     * behind the leftmost OTHER cloud. Without this guard a slightly
     * faster cloud will eventually catch the slower ones at the right
     * edge, and after wrapping it lines up just behind them again, then
     * catches up again, etc. — the whole flock ends up clumped on the
     * right side over a few minutes of play.
     *
     * @param {{ sprite: any, w: number }} self the wrapping cloud
     * @returns {number} new X (sprite anchor-centred)
     */
    _pickWrapX(self) {
        const leftEdge = this._bounds.x;
        const baseX = leftEdge - self.w / 2;

        let leftmostX = Infinity;
        for (const other of this._clouds) {
            if (other === self) continue;
            const s = other.sprite;
            if (!s || s.destroyed) continue;
            if (s.x < leftmostX) leftmostX = s.x;
        }

        if (!isFinite(leftmostX)) return baseX;

        const minGap = self.w * WRAP_MIN_GAP_FACTOR;
        // Need to land at least `minGap` behind the leftmost other cloud.
        const cap = leftmostX - minGap;
        return Math.min(baseX, cap);
    }

    destroy() {
        for (const c of this._clouds) {
            if (c.sprite && !c.sprite.destroyed) c.sprite.destroy();
        }
        this._clouds.length = 0;
        if (this.container && !this.container.destroyed) {
            if (this.container.parent) this.container.parent.removeChild(this.container);
            this.container.destroy({ children: true });
        }
    }

    _setBounds({ x, y, w, h }) {
        this._bounds = {
            x,
            y,
            w,
            // Clouds occupy only the top band of the playfield — keeps
            // the rest of the arena visually clean for gameplay.
            h: Math.max(80, h * SKY_BAND_FRAC),
        };
    }

    _build(themeManager) {
        if (!themeManager) return;
        const slots = CLOUD_DEFS.length;
        for (let i = 0; i < slots; i++) {
            const d = CLOUD_DEFS[i];
            const sprite = themeManager.createSprite(d.slot, d.w, d.h, { fitMode: 'contain' });
            // No procedural fallback by design — if the user clears the
            // slot the sky just stays empty, no random graphics show up.
            if (!sprite) continue;

            // Stagger initial X so the four clouds don't appear clumped.
            // Each cloud starts at a different fraction of the width with
            // a small extra offset so even neighbours don't line up.
            const startFrac = (i + 0.5) / slots;
            sprite.x = this._bounds.x + startFrac * this._bounds.w;
            sprite.y = this._bounds.y + d.yFrac * this._bounds.h;
            sprite.alpha = d.alpha;
            this.container.addChild(sprite);

            this._clouds.push({ sprite, speed: d.speed, w: d.w });
        }
    }
}
